import React from "react";
import * as M from "../BlankModal";
import * as MO from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as S from "../../../services";
import { LANG, T, TB, TC } from "../../../Constants";
import * as echarts from "echarts";

export type SankeyProps = {
    /** The current context */
    context: T.ContextParams;
    /** Callback to quit the modal */
    quit?: () => void;
    /** Extra params to style the modal */
    modal?: M.StyleModalProps;
}

// Legend type
type Legend = {
    nodes: {
        name: string;
        color: string;
    }[];
    links: {
        name: string;
        color: string;
    }[];
}

// Node type directly from the API
type Node = {
    name: string;
    color: string;
    type: string;
    tooltip: {
        name: string;
        value: number;
    }[];
    parents: string[];
    itemStyle?: {
        color: string;
    };
}

// Node type for the graph
type UpdatedNode = {
    color: string;
    itemStyle: {
        color: string;
    };
    tooltip: string;
    name: string;
    type: string;
    parents: string[];
}

// Link type directly from the API
type Link = {
    source: string;
    target: string;
    value: number;
    color: string;
}

const Sankey: React.FC<SankeyProps> = props => {
    const lg = H.useLanguage(); 
    const [error, set_error] = React.useState("");
    // Legend data directly from the API
    const [legend_data, set_legend_data, status] = H.useAsyncState<Legend>({ nodes: [], links: [] });
    // Legend data formatted for the legend
    const [legend, set_legend] = React.useState<Legend>({ nodes: [], links: [] });
    // Nodes data directly from the API
    const [nodes_static, set_nodes_static] = React.useState<Node[]>([]);
    // Nodes data for the graph
    const [nodes, set_nodes] = H.useAsyncState<UpdatedNode[]>([]);
    // Nodes data that we can modify
    const [updated_nodes, set_updated_nodes] = React.useState<UpdatedNode[]>([]);
    // Links data directly from the API
    const [links_static, set_links_static] = React.useState<Link[]>([]);
    // Links data for the graph
    const [links, set_links] = H.useAsyncState<Link[]>([]);
    // Links data that we can modify
    const [updated_links, set_updated_links] = React.useState<Link[]>([]);
    // Sum of the links values
    const [linksSum, setLinksSum] = React.useState<number>(0);
    // Local language value
    const local_lang_value = React.useMemo(() => LANG.getHtmlLanguageCode(lg.prop), [lg.prop]);
    // Chart reference
    const chartRef = React.useRef(null);
    // Time selection
    const [time, set_time] = React.useState<T.NRJ.TimeSelection>({ interval: "7 DAY" });
    // JSX elements for the legend
    const [legendContent, setLegendContent] = React.useState<JSX.Element[]>([]);
    // Selected nodes
    const [selectedNode, setSelectedNode] = React.useState<string[]>([]);
    // Selected links
    const [selectedLink, setSelectedLink] = React.useState<string[]>([]);
    // Focused node
    const [focus_node, setFocusNode] = React.useState<string>("");
    // list of nodes that are linked to the focus node
    const [focus_nodes_list, setFocusNodesList] = React.useState<UpdatedNode[]>([]);
    // Removed nodes
    const [removed_nodes, setRemovedNodes] = React.useState<UpdatedNode[]>([]);
    // Focused link
    const [focus_link, setFocusLink] = React.useState<string>("");
    // Remaining nodes
    const [remaining_nodes, setRemainingNodes] = React.useState<UpdatedNode[]>([]);
    // Report
    const [report, setReport] = React.useState<string>("");

    // Fetch the data from the API
    React.useEffect(() => {
        let isSubscribed = true;
        reset_legend();
        let period = TB.getFromTo(time);
        S.getSankey({ context: props.context, begin: period.from, end: period.to, is_origin: false })
            .then(({ data }) => {
                if (isSubscribed) {
                    set_error("");
                    set_nodes_static(data.json_data.nodes);
                    set_links_static(data.json_data.links);
                    set_legend_data(data.legend, "done");
                    setReport(data.report || "");
                }
            })
            .catch(error => {
                if (isSubscribed) {
                    set_nodes_static([]);
                    set_links_static([]);
                    setReport("");
                    set_legend_data({ nodes: [], links: [] }, "error");
                    set_error(error?.response?.data || error?.message);
                }
            });
        return () => {
            set_error("");
            isSubscribed = false;
            set_nodes_static([]);
            set_links_static([]);
            set_legend_data({ nodes: [], links: [] }, "load");
            setReport("");
        }
    }, [time, props.context, set_nodes, set_links, set_legend_data]);

    const reset_legend = React.useCallback(() => {
        document.querySelector<HTMLButtonElement>("#reset_legend_sankey")?.click?.();
        setSelectedNode([]);
        setSelectedLink([]);
        setFocusNode("");
        setFocusLink("");
    }, []);
    
    // Set the legend data
    React.useMemo(() => {
        legend_data.nodes.forEach(node => {
            node.name = lg.getStaticText(TC[node.name])
        });

        legend_data.links.forEach(link => {
            link.name = lg.getStaticText(TC[link.name])
        });

        set_legend(legend_data);

    }, [legend_data]);

    // Update the nodes to have a good format and to be sorted
    React.useEffect(() => {

        let updated_nodes = nodes_static.map((node) => {
            const tooltip_text = `${lg.getStaticText(TC[node.color])}<br>${
                node.tooltip
                    .map(item => `${lg.getStaticText(TC[('sankey_' + item.name).toUpperCase()])} : ${TB.formatNumber(item.value, local_lang_value)}`)
                    .join('<br>')
            }`;

            return {
                ...node,
                color: lg.getStaticText(TC[node.color]),
                itemStyle: {
                    color: legend.nodes.find(n => n.name === lg.getStaticText(TC[node.color]))?.color
                },
                tooltip: tooltip_text
            }
        });

        updated_nodes.sort((a, b) => {
            if (a.type < b.type) {
                return -1;
            }
            if (a.type > b.type) {
                return 1;
            }
            return 0;
        });
        
        set_nodes(updated_nodes);
    }, [nodes_static, legend]);

    // If there are no filters, then we can use the nodes data directly
    React.useEffect(() => {
        if (selectedNode.length === 0 && focus_node === "" && focus_link === "" && selectedLink.length === 0) {
            set_updated_nodes(nodes);
        }
    }, [nodes, selectedNode, selectedLink, focus_node, focus_link]);

    // Update the links to have a good format and to be sorted
    React.useEffect(() => {

        const updated_links = links_static.map((link) => {

            let widthValue = 1;

            if (link.value < 0) {
                widthValue = 5;
            }
            if (link.value == 0) {
                widthValue = 10;
            } else if (link.value > 10000000) {
                widthValue = 500;
            } else if (link.value > 1000000) {
                widthValue = 250;
            } else if (link.value > 100000) {
                widthValue = 150;
            } else if (link.value > 10000) {
                widthValue = 100;
            } else if (link.value > 1000) {
                widthValue = 50;
            } else if (link.value > 100) {
                widthValue = 25;
            } else if (link.value > 0) {
                widthValue = 15;
            } else {
                widthValue = 500;  // Catch-all for negative numbers (if intended)
            }
            setLinksSum(linksSum + link.value);

            return {
                ...link,
                color: lg.getStaticText(TC[link.color]), //req.data.translations[link.color.toLowerCase()]
                lineStyle: {
                    color: legend.links.find(l => l.name === lg.getStaticText(TC[link.color]))?.color,
                    opacity: 0.5,
                    normal: {
                        width: 1
                    }
                },
                trueValue: link.value,
                value: widthValue,
                
            }
        });

        updated_links.sort((a, b) => {
            if (a.color < b.color) {
                return -1;
            }
            if (a.color > b.color) {
                return 1;
            }
            return 0;
        });

        set_links(updated_links);
    }, [links_static, legend]);

    // If there are no filters, then we can use the links data directly
    React.useEffect(() => {
        if (selectedLink.length === 0 && focus_link === "") {
            set_updated_links(links);
        }
    }, [links, selectedLink, focus_link]);

    // Construct the sankey graph with echarts
    React.useEffect(() => {
        if (updated_nodes && updated_links && chartRef.current) {
            // Check if a chart instance already exists and dispose of it
            if (echarts.getInstanceByDom(chartRef.current)) {
                echarts.dispose(chartRef.current);
            }

            const chartInstance = echarts.init(chartRef.current, undefined, { height: Math.max((updated_nodes.length * 20), 700) });
            
            const sankeyChart = {
                tooltip: {
                    trigger: 'item',
                    triggerOn: 'mousemove',
                    formatter: function (params) {
                        if (params.dataType === 'edge') {
                            return `${params.data.color}<br>${params.data.source} -->  ${params.data.target} : <b>${TB.formatNumber(params.data.trueValue, local_lang_value)}</b>`;
                        }
                        else {
                            return `${params.data.tooltip_text}`;
                        }
                    }
                },
                legend: {},
                // layoutIterations: 0,
                animation: false,
                animationDuration: 0,
                series: {
                    type: 'sankey',
                    nodeGap: 10,
                    nodeWidth: 20,
                    emphasis: {
                        focus: 'adjacency',
                    },
                    lineStyle: {
                        color: 'gradient',
                        curveness: 0.4
                    },
                    label: {
                        show: true,
                        position: 'right',
                        distance: 10,
                    },
                    data: updated_nodes,
                    links: updated_links
                }
            };

            chartInstance.setOption(sankeyChart);
        }
    }, [updated_nodes, updated_links]);
    

    const renderLegendNodes = React.useCallback(() => {
        return (
            <>
                <div style={{ fontSize: '1.2em', fontWeight: 'bold', marginBottom: 5 }}>{lg.getStaticText(TC.LT_NODES)}</div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
                    {legend.nodes.map(node => (
                        <div key={node.name} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            cursor: 'pointer',
                            backgroundColor: focus_node === node.name ? '#f5f5f5' : 'transparent',
                            opacity: focus_node && focus_node !== node.name ? 0.5 : 1,
                            transition: 'all 0.3s',
                            padding: '3px 4px',
                            borderRadius: '4px'
                        }} onClick={(e) => {handleNodeClick(e, node.name);}} onContextMenu={(e) => {handleNodeClick(e, node.name);}}>
                            <div style={{ 
                                width: 12, 
                                height: 12, 
                                backgroundColor: node.color, 
                                marginRight: 5, 
                                opacity: selectedNode.includes(node.name) ? 0.5 : 1, 
                                transition: 'opacity 0.3s' 
                            }}></div>
                            <span style={{ 
                                fontSize: '1.2em',
                                opacity: selectedNode.includes(node.name) ? 0.5 : 1,
                                transition: 'opacity 0.3s'
                            }}>{node.name}</span>
                        </div>
                    ))}
                </div>
            </>
        );
    }, [legend, selectedNode, updated_nodes, nodes, focus_node]);

    const handleNodeClick = React.useCallback((e: React.MouseEvent, selectedNodeColor: string) => {
        e.stopPropagation();

        if (e.button === 0 && focus_node === "" && focus_link === "") {
            const newSelectedNodes = selectedNode.includes(selectedNodeColor) 
            ? selectedNode.filter(name => name !== selectedNodeColor) 
            : [...selectedNode, selectedNodeColor];
        
            setSelectedNode(newSelectedNodes);

            // Get all nodes that are selected
            let selectedNodes = [];
            nodes.forEach(node => {
                if (newSelectedNodes.includes(node.color)) {
                    selectedNodes.push(node);
                }
            });

            // for each selected node, get all nodes that are connected to it
            let filteredNodes = [];
            selectedNodes.forEach(node => {
                let connectedNodes = [];
                const connectedLinks = links.filter(link => link.source === node.name || link.target === node.name);
                connectedLinks.forEach(link => {
                    connectedNodes.push(link.source === node.name ? link.target : link.source);
                });
                const isConnectedNodeSelected = connectedNodes.every(connectedNode => selectedNodes.some(selectedNode => selectedNode.name === connectedNode));
                if (isConnectedNodeSelected) {
                    filteredNodes.push(node);
                }
            });

            // Get updated nodes and remove filteredNodes from it
            let keepedNodes = nodes.filter(node => !filteredNodes.some(filteredNode => filteredNode.name === node.name));
            
            // Get the links that are connected to the keeped nodes
            let connectedLinks = updated_links.filter(link => keepedNodes.some(updatedNodes => updatedNodes.name === link.source || updatedNodes.name === link.target));
            // Keep nodes that 

            // Get the keeped nodes that are in the connectedLinks
            let keepedNodesInConnectedLinks = keepedNodes.filter(node => connectedLinks.some(link => link.source === node.name || link.target === node.name));

            set_updated_nodes(keepedNodesInConnectedLinks);
        }
        else if (e.button === 0 && focus_node === "" && focus_link) {
            const newSelectedNodes = selectedNode.includes(selectedNodeColor) 
            ? selectedNode.filter(name => name !== selectedNodeColor) 
            : [...selectedNode, selectedNodeColor];
        
            setSelectedNode(newSelectedNodes);

            // Get all nodes that are selected
            let selectedNodes = [];
            remaining_nodes.forEach(node => {
                if (newSelectedNodes.includes(node.color)) {
                    selectedNodes.push(node);
                }
            });

            // for each selected node, get all nodes that are connected to it
            let filteredNodes = [];
            selectedNodes.forEach(node => {
                let connectedNodes = [];
                const connectedLinks = links.filter(link => link.source === node.name || link.target === node.name);
                connectedLinks.forEach(link => {
                    connectedNodes.push(link.source === node.name ? link.target : link.source);
                });
                const isConnectedNodeSelected = connectedNodes.every(connectedNode => selectedNodes.some(selectedNode => selectedNode.name === connectedNode));
                if (isConnectedNodeSelected) {
                    filteredNodes.push(node);
                }
            });

            // Get updated nodes and remove filteredNodes from it
            let updatedNodes = remaining_nodes.filter(node => !filteredNodes.some(filteredNode => filteredNode.name === node.name));
            set_updated_nodes(updatedNodes);
        }
        else if (e.button === 2) {
            e.preventDefault();
            e.stopPropagation();
            setFocusLink("");
            setSelectedNode([]);
            let newSelectedNodeColor = selectedNodeColor === focus_node ? "" : selectedNodeColor;
            if (newSelectedNodeColor === "") setSelectedLink([]);
            setFocusNode(newSelectedNodeColor);
            const focusNodeOnly = nodes.filter(node => node.color === newSelectedNodeColor);
            const removedNodes = nodes.filter(node => node.color !== newSelectedNodeColor);
            const focusNodeOnlyFiltered = focusNodeOnly.filter(node => {
                const link = links.filter(link => link.source === node.name || link.target === node.name);
                const isConnectedToRemovedNodes = link.every(link => removedNodes.some(removedNode => removedNode.name === link.source || removedNode.name === link.target));
                return !isConnectedToRemovedNodes;
            });

            set_updated_nodes(focusNodeOnlyFiltered);
            setFocusNodesList(focusNodeOnlyFiltered);
        }
    }, [selectedNode, nodes, links, updated_nodes, focus_node, focus_link, remaining_nodes]);

    const renderLegendLinks = React.useCallback(() => {
        return (
            <>
                <div style={{ fontSize: '1.2em', fontWeight: 'bold', marginBottom: 5 }}>{lg.getStaticText(TC.LT_LINKS)}</div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
                    {legend.links.map(link => (
                        <div key={link.name} 
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                cursor: 'pointer', 
                                backgroundColor: focus_link === link.name ? '#f5f5f5' : 'transparent',
                                padding: '3px 4px', 
                                opacity: focus_link && focus_link !== link.name ? 0.5 : 1,
                                transition: 'opacity 0.3s',
                                borderRadius: '4px',
                            }} 
                            onClick={(e) => {handleLinkClick(e, link.name);}} 
                            onContextMenu={(e) => {handleLinkClick(e, link.name);}}
                            >
                            <div style={{ 
                                width: 12, 
                                height: 12, 
                                backgroundColor: link.color, 
                                marginRight: 5,
                                opacity: selectedLink.includes(link.name) ? 0.5 : 1,
                                transition: 'opacity 0.3s'
                            }}></div>
                            <span style={{ 
                                fontSize: '1.2em',
                                opacity: selectedLink.includes(link.name) ? 0.5 : 1,
                                transition: 'opacity 0.3s'
                            }}>{link.name}</span>
                        </div>
                    ))}
                </div>
            </>
        );
    }, [legend, selectedLink, updated_links, links, updated_nodes, removed_nodes, focus_link]);

    const handleLinkClick = React.useCallback((e: React.MouseEvent,linkName: string) => {
        e.stopPropagation();

        if (e.button === 0 && focus_link === "" && focus_node === "") {
            const newSelectedLinks = selectedLink.includes(linkName) 
                ? selectedLink.filter(name => name !== linkName) 
                : [...selectedLink, linkName];
            setSelectedLink(newSelectedLinks);
            
            // Links that we keep
            const filtered_links = links.filter(link => !newSelectedLinks.includes(link.color));

            // Retrieve all unique names of source and target of filtered links
            let filtered_nodes_names = filtered_links.map(link => link.source).concat(filtered_links.map(link => link.target));
            filtered_nodes_names = [...new Set(filtered_nodes_names)];

            // Retrieve all nodes that are link.source or link.target of filtered_links
            let filtered_nodes = nodes.filter(node => filtered_nodes_names.includes(node.name));

            set_updated_links(filtered_links);
            set_updated_nodes(filtered_nodes);
        }
        else if (e.button === 0 && focus_link === "" && focus_node) {

            const newSelectedLinks = selectedLink.includes(linkName) 
                ? selectedLink.filter(name => name !== linkName) 
                : [...selectedLink, linkName];
            setSelectedLink(newSelectedLinks);
            
            // Links that we keep
            const filtered_links = links.filter(link => !newSelectedLinks.includes(link.color));

            // Retrieve all unique names of source and target of filtered links
            let filtered_nodes_names = filtered_links.map(link => link.source).concat(filtered_links.map(link => link.target));
            filtered_nodes_names = [...new Set(filtered_nodes_names)];

            // Retrieve all nodes that are link.source or link.target of filtered_links
            let filtered_nodes = focus_nodes_list.filter(node => filtered_nodes_names.includes(node.name));

            // Retrieve links that are in filtered_links and source and target is in filtered_nodes
            let filtered_links_with_filtered_nodes = filtered_links.filter(link => 
                filtered_nodes.some(node => node.name === link.source) && 
                filtered_nodes.some(node => node.name === link.target)
            );

            // Keep nodes that are in filtered_links_with_filtered_nodes
            let filtered_nodes_without_links = filtered_nodes.filter(node => filtered_links_with_filtered_nodes.some(link => link.source === node.name || link.target === node.name));

            set_updated_links(filtered_links_with_filtered_nodes);
            set_updated_nodes(filtered_nodes_without_links);
        }
        else if (e.button === 2) {
            e.preventDefault();
            e.stopPropagation();
            setFocusNode("");
            setSelectedLink([]);
            setSelectedNode([]);
            setRemovedNodes([]);
            let newSelectedLinkColor = linkName === focus_link ? "" : linkName;
            setFocusLink(newSelectedLinkColor);
            const focusLinkOnly = links.filter(link => link.color === newSelectedLinkColor);

            //retrive the nodes that are connected to the focus link
            if (newSelectedLinkColor !== "") {
                const focusLinkNodes = nodes.filter(node => focusLinkOnly.some(link => link.source === node.name || link.target === node.name));
                set_updated_nodes(focusLinkNodes);
                setRemainingNodes(focusLinkNodes);
            }

            set_updated_links(focusLinkOnly);
        }

    }, [selectedLink, updated_links, links, updated_nodes, nodes, focus_node, removed_nodes, focus_link]);


    React.useEffect(() => {
        const nodesLegend = [renderLegendNodes()];
        const linksLegend = [renderLegendLinks()];
        setLegendContent([...nodesLegend, ...linksLegend]);
    }, [legend, renderLegendNodes, renderLegendLinks]);

    const downloadReport = React.useCallback(() => {
        S.getContextName(props.context)
        .then(({ data }) => {
            const blob = new Blob([report], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${lg.getStaticText(TC.SANKEY_BUILDING_FLOW)} - ${data}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        })
        .catch(MO.Alerts.loadError);
    }, [report, lg]);


    return <M.default
        {...props.modal}
        onQuit={props.quit}
        size={props.modal?.size || "lg"}
        
        title={props.modal?.title || TC.DATA_ORGANIZER_SANKEY_TITLE}
        isFullScreen={typeof props.modal?.isFullScreen === "boolean" ? props.modal.isFullScreen : true}
    >
        <C.Flex>
            <C.TimeSelector
                to={time.to}
                from={time.from}
                interval={time.interval}
                onChangeDatePicker={time => set_time(time)}
                onChangeInterval={interval => set_time({ interval })}
            />

            <C.Button
                icon="history"
                className="ms-2"
                onClick={reset_legend}
                variant="outline-primary"
                text={TC.SANKEY_RESET_LEGEND}
            />

            <C.Button
                icon="save"
                className="ms-2"
                onClick={downloadReport}
                variant="outline-primary"
                text={TC.SANKEY_DOWNLOAD_REPORT}
            />
        </C.Flex>

        <div id="legend_sankey" className="mt-3">
            {legendContent}
        </div>

        <C.Spinner min_load_size="700px" status={status} error={error}>
            <div ref={chartRef}></div>
        </C.Spinner>
    </M.default>;
}

export default Sankey;
